<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();
const getCardByConversationId = useMapGetter(
  'kanban/cards/getCardByConversationId'
);
const getListsByBoard = useMapGetter('kanban/lists/getListsByBoard');

const card = computed(() =>
  getCardByConversationId.value(props.conversationId)
);
const boardLists = computed(() =>
  card.value ? getListsByBoard.value(card.value.kanban_board_id) : []
);
const currentIndex = computed(() =>
  boardLists.value.findIndex(list => list.id === card.value?.kanban_list_id)
);

const pendingIndex = ref(null);
const isSaving = ref(false);

// This panel is usually reached by opening a conversation directly, where nothing else has
// populated the kanban store -- KanbanBoard and the settings screens are the only other fetchers,
// and neither is mounted here. Without this the panel always claimed the conversation had no card.
// There is no fetch-one-card-by-conversation endpoint, so cards are fetched unscoped (index's
// scope_by_board no-ops when kanban_board_id is absent) and only then can we know which board's
// lists to load.
onMounted(async () => {
  try {
    await store.dispatch('kanban/cards/get');
    const boardId = card.value?.kanban_board_id;
    if (boardId) await store.dispatch('kanban/lists/get', boardId);
  } catch (error) {
    useAlert(t('KANBAN.CONVERSATION_PANEL.LOAD_ERROR'));
  }
});

const displayedIndex = computed(() => pendingIndex.value ?? currentIndex.value);
const displayedList = computed(() => boardLists.value[displayedIndex.value]);
const hasPendingChange = computed(
  () => pendingIndex.value !== null && pendingIndex.value !== currentIndex.value
);

const goPrevious = () => {
  pendingIndex.value = Math.max(0, displayedIndex.value - 1);
};

const goNext = () => {
  pendingIndex.value = Math.min(
    boardLists.value.length - 1,
    displayedIndex.value + 1
  );
};

const confirmMove = async () => {
  if (!hasPendingChange.value) return;
  isSaving.value = true;
  try {
    await store.dispatch('kanban/cards/update', {
      id: card.value.id,
      kanban_list_id: displayedList.value.id,
    });
    useAlert(t('KANBAN.CONVERSATION_PANEL.MOVE_SUCCESS'));
    pendingIndex.value = null;
  } catch (error) {
    useAlert(t('KANBAN.CONVERSATION_PANEL.MOVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-2 px-4 py-3">
    <p v-if="!card" class="text-label-small text-n-slate-11">
      {{ t('KANBAN.CONVERSATION_PANEL.NO_CARD') }}
    </p>
    <div v-else class="flex flex-col gap-2">
      <span class="text-label-small text-n-slate-11">
        {{ t('KANBAN.CONVERSATION_PANEL.CURRENT_STAGE') }}
      </span>
      <span class="text-body-main text-n-slate-12 font-medium">
        {{ displayedList?.title }}
      </span>
      <div class="flex items-center gap-2">
        <Button
          data-test-id="kanban-stage-prev"
          icon="i-lucide-chevron-left"
          slate
          sm
          :disabled="displayedIndex <= 0"
          :title="t('KANBAN.CONVERSATION_PANEL.PREVIOUS')"
          @click="goPrevious"
        />
        <Button
          data-test-id="kanban-stage-next"
          icon="i-lucide-chevron-right"
          slate
          sm
          :disabled="displayedIndex >= boardLists.length - 1"
          :title="t('KANBAN.CONVERSATION_PANEL.NEXT')"
          @click="goNext"
        />
        <Button
          v-if="hasPendingChange"
          data-test-id="kanban-stage-confirm"
          :label="t('KANBAN.CONVERSATION_PANEL.CONFIRM')"
          size="sm"
          :is-loading="isSaving"
          @click="confirmMove"
        />
      </div>
    </div>
  </div>
</template>
